import React, { useMemo } from 'react';
import { Card, Skeleton, Typography } from 'antd';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip as RechartsTooltip, LabelList } from 'recharts';
import { evalColors, LegendItem, normalizeToPercent } from './summarizationUtils';
import { Button, TruncatedText } from '@spark';

const { Text } = Typography;

const BAR_SIZE = 12;
const BAR_RADIUS = 3;

const LLMJudgeBreakdownChart = ({
  data,
  onConfigure,
  showTeacher = true,
  showGroundTruth = true,
  loading = false,
  teacherLoading = false,
  teacherBarLoadingText,
  overallScoresContent,
}) => {
  const teacherLabel =
    data?.models?.find?.((m) => m?.key === 'teacher')?.label || data?.models?.[0]?.label || 'Teacher';
  const studentLabel =
    data?.models?.find?.((m) => m?.key === 'student')?.label || data?.models?.[1]?.label || 'Specific AI';
  const groundTruthLabel =
    data?.models?.find?.((m) => m?.key === 'ground_truth')?.label || 'Benchmark';

  const hasGroundTruth = useMemo(() => {
    const metrics = data?.metrics || [];
    return metrics.some((m) => m?.ground_truth != null && Number.isFinite(Number(m.ground_truth)));
  }, [data?.metrics]);

  const chartData = useMemo(() => {
    const metrics = data?.metrics || [];
    return metrics.map((m) => ({
      name: m.label,
      teacher: teacherLoading ? 100 : normalizeToPercent(m.teacher),
      student: normalizeToPercent(m.student),
      ground_truth: normalizeToPercent(m.ground_truth),
    }));
  }, [data, teacherLoading]);

  const categoryCount = chartData.length;
  const chartHeight = Math.max(220, 80 * categoryCount);

  const fmt = (v) => {
    const n = typeof v === 'number' ? v : Number(v);
    if (!Number.isFinite(n)) return '';
    if (Math.abs(n) < 1e-9) return '';
    const rounded = Math.round(n * 100) / 100;
    return rounded
      .toFixed(2)
      .replace(/\.00$/, '')
      .replace(/(\.\d)0$/, '$1');
  };

  const fixedRightLabel = (props) => {
    const { x, y, width, height, value } = props;
    const label = fmt(value);
    if (!label) return null;
    const n = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(n) || Math.abs(n) < 1e-9) return null;
    const plotRight = x + width * (100 / n);
    return (
      <text x={plotRight + 10} y={y + height / 2 + 4} fill="#6b7280" fontSize={12} fontWeight={400}>
        {label}
      </text>
    );
  };

  const teacherLoadingBarShape = (props) => {
    const { x, y, width, height } = props;
    const dotR = 4;
    const cy = y + height / 2;
    return (
      <g>
        <circle cx={x + dotR + 2} cy={cy} r={dotR} fill={evalColors.teacher} opacity={0.7} />
        <text x={x + dotR * 2 + 10} y={cy + 4} fill="#9ca3af" fontSize={12} fontWeight={400}>
          {teacherBarLoadingText || 'Getting response...'}
        </text>
        <text x={x + width + 10} y={cy + 4} fill="#d1d5db" fontSize={12} fontWeight={400}>
          In progress
        </text>
      </g>
    );
  };

  return (
    <Card
      className="fs-unmask"
      style={{ borderRadius: 8, border: '1px solid #e0e0e0' }}
      styles={{ body: { padding: 24 } }}
    >
      <Text style={{ fontWeight: 600, fontSize: 18, display: 'block' }}>LLM as a Judge</Text>

      {overallScoresContent && (
        <div style={{ marginTop: 12 }}>{overallScoresContent}</div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20, marginBottom: 8 }}>
        <Text style={{ color: '#8c8c8c', fontSize: 14, fontWeight: 400 }}>Scores per Criteria:</Text>
        <div style={{ display: 'flex', alignItems: 'center', gap: 72 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 36 }}>
            {showGroundTruth && hasGroundTruth && (
              <LegendItem color={evalColors.ground_truth} label={groundTruthLabel} />
            )}
            <LegendItem color={evalColors.student} label={studentLabel} />
            {showTeacher && (
              <LegendItem color={evalColors.teacher} label={teacherLabel} />
            )}
          </div>
          <Button
            variant="link"
            size="small"
            onClick={loading ? undefined : onConfigure}
          >
            Configure
          </Button>
        </div>
      </div>

      {loading ? (
        <div style={{ height: chartHeight, paddingTop: 16 }}>
          <Skeleton active paragraph={{ rows: Math.max(3, categoryCount * 2) }} />
        </div>
      ) : (
        <div style={{ height: chartHeight }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 10, right: 72, left: 20, bottom: 10 }}
              barCategoryGap={30}
              barGap={6}
            >
              <CartesianGrid stroke="#f3f4f6" horizontal={false} vertical={false} />
              <XAxis type="number" domain={[0, 100]} tick={{ fill: '#6b7280', fontSize: 12 }} />
              <YAxis
                type="category"
                dataKey="name"
                width={120}
                tick={({ x, y, payload }) => (
                  <foreignObject x={x - 118} y={y - 10} width={115} height={20}>
                    <TruncatedText
                      text={payload.value}
                      maxWidth={115}
                      style={{ color: '#6b7280', fontSize: 12, lineHeight: '20px' }}
                    />
                  </foreignObject>
                )}
              />
              <RechartsTooltip
                formatter={(v, name) => {
                  if (teacherLoading && name === teacherLabel) return ['In progress', name];
                  return [fmt(v), `${name} Score`];
                }}
                contentStyle={{ borderRadius: 8 }}
              />
              {showGroundTruth && hasGroundTruth && (
                <Bar dataKey="ground_truth" name={groundTruthLabel} fill={evalColors.ground_truth} radius={[0, BAR_RADIUS, BAR_RADIUS, 0]} barSize={BAR_SIZE}>
                  <LabelList dataKey="ground_truth" content={fixedRightLabel} />
                </Bar>
              )}
              <Bar dataKey="student" name={studentLabel} fill={evalColors.student} radius={[0, BAR_RADIUS, BAR_RADIUS, 0]} barSize={BAR_SIZE}>
                <LabelList dataKey="student" content={fixedRightLabel} />
              </Bar>
              {showTeacher && (
                <Bar
                  dataKey="teacher"
                  name={teacherLabel}
                  fill={teacherLoading ? 'transparent' : evalColors.teacher}
                  radius={teacherLoading ? undefined : [0, BAR_RADIUS, BAR_RADIUS, 0]}
                  barSize={BAR_SIZE}
                  shape={teacherLoading ? teacherLoadingBarShape : undefined}
                >
                  {!teacherLoading && (
                    <LabelList dataKey="teacher" content={fixedRightLabel} />
                  )}
                </Bar>
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
};

export default LLMJudgeBreakdownChart;
